import { arrayIncludes } from '../override-utils';
import { API_KEY } from '@aesop-engineering/constants';

/**
 * Checks if the current locale has chat support
 * @returns boolean
 */
export const isChatSupported = () =>
  /^\/(ca|au|us|uk|jp|fr|nz|sg|de|hk|tw|ch|kr|dk|it|no|se)/.test(
    window.location.pathname
  );

const chatLabel = {
  german: 'Live-Support Chat',
  french: 'Chat en direct',
  japanese: 'ライブ アシスタンス',
  korean: '1:1 채팅 상담',
  simplifiedChinese: '在线服务',
  traditionalChinese: '線上服務',
  fallback: 'Live Assistance',
};

export const defaultDepartments = {
  au: 28097147,
  nz: 28097147,
  jp: 360001127715,
  ca: 360001114776,
  us: 35749147,
  sg: 360001155355,
  kr: 43063387,
  hk: [
    { id: 360001844075, locale: 'en' },
    { id: 360001844775, locale: 'zh_cn' },
    360001830096,
  ],
  ch: [
    { id: 360001127555, locale: 'en' },
    { id: 360001127515, locale: 'fr' },
    { id: 360001213756, locale: 'de' },
  ],
  tw: 360002303476,
  uk: 35836187,
  fr: [{ id: 360001127555, locale: 'en' }, 360001127515],
  de: [{ id: 360001127555, locale: 'en' }, 360001213756],
  fallback: 360001111415,
  englishTeam: 360003916656,
  dachTeam: 38024728,
  franceTeam: 36877308,
  asiaTeam: 42645328,
};

export const getCountryCodeAndLocale = () => {
  return window.location.pathname.split('/').filter((string) => !!string); // remove empty strings
};

export const isChatOnline = () => {
  const [countryCode, locale] = getCountryCodeAndLocale();
  const countryDept = getCountryDepartment(countryCode, locale);

  if (countryDept === undefined) {
    return false;
  }
  return countryDept.status === 'online' ? true : false;
};

const getCountryDepartment = (country, locale) => {
  if (arrayIncludes(['hk', 'fr', 'ch'], country)) {
    const possibleDepartments = defaultDepartments[country].filter(
      (region) => region.locale === locale
    );
    if (possibleDepartments.length > 0) {
      return window.zE(
        'webWidget:get',
        'chat:department',
        possibleDepartments[0].id
      );
    }
    const fallbackDepartment = defaultDepartments[country].slice(-1)[0];
    return window.zE('webWidget:get', 'chat:department', fallbackDepartment);
  }
  return window.zE(
    'webWidget:get',
    'chat:department',
    defaultDepartments[country]
  );
};

export const getLocalisedChatLabel = () => {
  const [countryCode, locale] = getCountryCodeAndLocale();

  switch (countryCode) {
    case 'ca':
      if (locale === 'fr') return chatLabel.french;
      return chatLabel.fallback;
    case 'de':
      return chatLabel.german;
    case 'fr':
      return chatLabel.french;
    case 'ch':
      if (locale === 'fr') return chatLabel.french;
      if (locale === 'de') return chatLabel.german;
      return chatLabel.fallback;
    case 'jp':
      return chatLabel.japanese;
    case 'kr':
      return chatLabel.korean;
    case 'tw':
      return chatLabel.traditionalChinese;
    case 'hk':
      if (locale === 'zh_hant') return chatLabel.traditionalChinese;
      if (locale === 'cn') return chatLabel.simplifiedChinese;
      return chatLabel.fallback;
    default:
      return chatLabel.fallback;
  }
};

export const getWidgetSettings = (
  talkNickname,
  talkSuppress,
  departmentList,
  departmentSelect,
  tag,
  country
) => ({
  analytics: false,
  helpCenter: {
    suppress: arrayIncludes(['hk', 'sg', 'jp', 'tw', 'kr'], country),
    chatButton: {
      '*': 'Assistance',
    },
    searchPlaceholder: {
      '*': 'What are you enquiring about?',
      ja: '何についてお問合せされますか?',
      ko: '무엇을 도와드릴까요?',
      'fr-ca': 'Votre question',
      'fr-fr': 'Votre question',
      'de-fr': 'Votre question',
      de: 'Art der Anfrage',
      'de-ch': 'Art der Anfrage',
      'zh-cn': '你在询问',
      'zh-hk': '你在询问',
      'zh-tw': '你在询问',
    },
  },
  webWidget: {
    talk: {
      nickname: talkNickname,
      suppress: talkSuppress,
      title: {
        '*': 'Call Us',
        ja: '電話',
        'zh-cn': '致电我们',
        'zh-hk': '致電我們',
        'zh-tw': '致電我們',
      },
    },
    zIndex: 1000,
    offset: {
      vertical: '50px',
      mobile: {
        vertical: '30px',
      },
    },
    contactOptions: {
      enabled: true,
      contactButton: {
        '*': 'Contact Us',
        ja: '問い合わせ',
        ko: '문의 하기',
        'fr-ca': 'Nous contacter',
        'fr-fr': 'Nous contacter',
        'de-fr': 'Nous contacter',
        de: 'Kontaktieren Sie uns',
        'de-ch': 'Kontaktieren Sie uns',
        'zh-cn': '联络我们',
        'zh-hk': '聯絡我們',
        'zh-tw': '聯絡我們',
      },
      chatLabelOnline: {
        '*': chatLabel.fallback,
        ja: chatLabel.japanese,
        ko: chatLabel.korean,
        'fr-ca': chatLabel.french,
        'fr-fr': chatLabel.french,
        'de-fr': chatLabel.french,
        de: chatLabel.german,
        'de-ch': chatLabel.german,
        'zh-cn': chatLabel.simplifiedChinese,
        'zh-hk': chatLabel.traditionalChinese,
        'zh-tw': chatLabel.traditionalChinese,
      },
      contactFormLabel: {
        '*': 'Send us a message',
        ja: 'お問い合わせを送る',
        ko: '이메일 문의 하기',
        'fr-ca': 'Nous envoyer un message',
        'fr-fr': 'Nous envoyer un message',
        'de-fr': 'Nous envoyer un message',
        de: 'Senden Sie uns eine Nachricht',
        'de-ch': 'Senden Sie uns eine Nachricht',
        'zh-cn': '寄讯息给我们',
        'zh-hk': '寄訊息給我們',
        'zh-tw': '寄訊息給我們',
      },
    },
    color: {
      header: '#FFFEF2',
      launcherText: '#FFFEF2',
      button: '#333',
      launcher: '#333',
      articleLinks: '#333',
      resultLists: '#333',
    },
    launcher: {
      label: {
        '*': ' ',
      },
      chatLabel: {
        '*': '',
      },
      badge: {
        layout: 'text_only',
        label: {
          '*': 'Live Assistance',
          ja: 'ライブ アシスタンス',
          ko: '1:1 채팅 상담',
          'fr-ca': 'Chat en direct',
          'fr-fr': 'Chat en direct',
          'de-fr': 'Chat en direct',
          de: 'Live-Support Chat',
          'de-ch': 'Live-Support Chat',
          'zh-cn': '在线服务',
          'zh-hk': '線上服務',
          'zh-tw': '線上服務',
        },
      },
    },
    chat: {
      title: {
        '*': 'Live Assistance',
        ja: 'ライブ アシスタンス',
        ko: '1:1 채팅 상담',
        'fr-ca': 'Chat en direct',
        'fr-fr': 'Chat en direct',
        'de-fr': 'Chat en direct',
        de: 'Live-Support Chat',
        'de-ch': 'Live-Support Chat',
        'zh-cn': '在线服务',
        'zh-hk': '線上服務',
        'zh-tw': '線上服務',
      },
      prechatForm: {
        greeting: {
          ja:
            'こちらの＜チャット＞を始めることで、イソップのウェブサイト利用規約とプライバシーポリシーをご確認され、ご承諾いただいたものとみなします。',
          ko:
            '상담을 시작하기에 앞서 이용약관, 개인정보 수집 및 이용에 대하여 고객님의 동의가 필요합니다. 상담 전 고객님의 동의여부 확인 후 상담이 시작됩니다. ※이용약관, 개인정보 수집 및 이용에 대하여 동의를 거부할 수 있으며, 이 경우 1:1 채팅상담 서비스 이용이 불가합니다.',
          'fr-ca':
            'Cher client(e), en débutant une conversation avec nous, vous reconnaissez avoir lu et accepté nos conditions générales de vente et notre politique de confidentialité relative aux données personnelles. ',
          'fr-fr':
            'Cher client(e), en débutant une conversation avec nous, vous reconnaissez avoir lu et accepté nos conditions générales de vente et notre politique de confidentialité relative aux données personnelles. ',
          'de-fr':
            'Cher client(e), en débutant une conversation avec nous, vous reconnaissez avoir lu et accepté nos conditions générales de vente et notre politique de confidentialité relative aux données personnelles. ',
          de:
            'Sobald Sie eine Unterhaltung mit uns beginnen, bestätigen sie, dass Sie unsere Vertragsbedingungen gelesen haben und unsere Datenschutzbestimmungen akzeptieren. ',
          'de-ch':
            'Sobald Sie eine Unterhaltung mit uns beginnen, bestätigen sie, dass Sie unsere Vertragsbedingungen gelesen haben und unsere Datenschutzbestimmungen akzeptieren. ',
          'zh-cn':
            '当您展开对话时，即确认您已阅读并同意我们的条款及细则和私隐政策声明。',
          'zh-hk':
            '當您展開對話時，即確認您已閱讀並同意我們的條款及細則和私隱政策聲明。',
          'zh-tw':
            '當您展開對話時，即確認您已閱讀並同意我們的條款及細則和私隱政策聲明。',
        },
        departmentLabel: {
          ja: 'お問合せ内容について',
          ko: '무엇을 도와드릴까요?',
          'fr-ca': 'Choisissez un problème',
          'fr-fr': 'Choisissez un problème',
          'de-fr': 'Choisissez un problème',
          de: 'Art Ihrer Anfrage',
          'de-ch': 'Art Ihrer Anfrage',
          'zh-cn': '查询类型',
          'zh-hk': '查詢類型',
          'zh-tw': '查詢類型',
        },
      },
      hideWhenOffline: false,
      concierge: {
        avatarPath:
          'https://v2assets.zopim.io/v7PmZTrYbpw08wT6eBWbmVlk5Y8g1g1j-concierge?1539843422567',
        name: 'Aesop',
        title: { '*': 'Customer Service' },
      },
      departments: {
        enabled: departmentList,
        select: departmentSelect,
      },
      suppress: false,
      tags: [tag],
      notifications: {
        mobile: {
          disable: true,
        },
      },
    },
  },
});

export const getKey = (market, useZendeskSandbox) => {
  if (useZendeskSandbox) {
    return API_KEY.zendeskSandboxKey;
  }

  const marketRegion = market[2];
  const marketLanguage = market[4];

  if (marketLanguage === 'es') {
    return API_KEY.zendeskEsKey;
  }

  if (
    (marketRegion === 'fr' || marketRegion === 'de') &&
    marketLanguage === 'en'
  ) {
    return API_KEY.zendeskFrDeEnKey;
  }

  if (marketRegion === 'fr' && marketLanguage === 'fr') {
    return API_KEY.zendeskFrFrKey;
  }

  if (marketRegion === 'de' && marketLanguage === 'de') {
    return API_KEY.zendeskDeDeKey;
  }

  switch (marketRegion) {
    case 'jp':
      return API_KEY.zendeskJpKey;
    case 'us':
      return API_KEY.zendeskUsKey;
    case 'ca':
      return API_KEY.zendeskCaKey;
    case 'au':
      return API_KEY.zendeskAuKey;
    case 'nz':
      return API_KEY.zendeskNzKey;
    case 'ch':
      return API_KEY.zendeskChKey;
    case 'dk':
      return API_KEY.zendeskDkKey;
    case 'hk':
      return API_KEY.zendeskHkKey;
    case 'it':
      return API_KEY.zendeskItKey;
    case 'kr':
      return API_KEY.zendeskKrKey;
    case 'no':
      return API_KEY.zendeskNoKey;
    case 'sg':
      return API_KEY.zendeskSgKey;
    case 'se':
      return API_KEY.zendeskSeKey;
    case 'tw':
      return API_KEY.zendeskTwKey;
    case 'uk':
      return API_KEY.zendeskGbKey;
    default:
      return API_KEY.zendeskDefaultKey;
  }
};
